import { useRef } from 'react';
import { useProperty, useTranslation } from '@eti/providers';
import { ProductCardLayoutType } from '../models/product';
import { useLinksAccessibility } from '../../custom-hooks/useLinkAccessibility';
import getTranslatedTexts from '../text-resolvers/utils/getTranslatedTexts';
import { getModal, getPriceProps, getReadMoreLabel, getShouldDisableValidation, getShouldEnableAddToCartButton, getSingleChoiceProps, getUpsellProps } from '../utils/productCardProps';
import { useIllustration, usePictogram } from '../utils/useIllustration';
import { useProductSnowplowTracking } from '../../custom-hooks/useProductSnowplowTracking';
import { useProductGADataLayer } from '../../custom-hooks/useProductGADataLayer';
import { getProduct } from '../../single-choice/utils/singleChoiceTransformer';
import { isOrderTypePostbooking } from '../../models';
export const useProductData = ({
  productId,
  order,
  productName,
  productCode,
  productPosition,
  isSelected,
  form,
  sellSpecification,
  texts,
  productType = 'none',
  layoutType
}) => {
  var _product$priceRaw, _order$persuasion;
  const {
    p
  } = useProperty();
  const {
    t,
    nonDebugT
  } = useTranslation();
  const illustrationUrl = useIllustration(productName);
  const pictogramUrl = usePictogram(productName);
  const rootElement = useRef(null);
  const snowplowTracking = useProductSnowplowTracking(productName, {
    productPosition,
    rootElement
  });
  const translation = getTranslatedTexts(texts, t);
  const readMoreButtonLabel = getReadMoreLabel(t);
  const extraProps = getSingleChoiceProps(t, p, nonDebugT);
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: translation.title
  });
  const product = getProduct(productId, order, productType);
  const shouldEnableAddToCartButton = getShouldEnableAddToCartButton(p, productCode, layoutType, isOrderTypePostbooking(order));
  const {
    sendCartGAEvent
  } = useProductGADataLayer({
    productId,
    productCode,
    productName,
    priceRaw: (_product$priceRaw = product === null || product === void 0 ? void 0 : product.priceRaw) !== null && _product$priceRaw !== void 0 ? _product$priceRaw : 0,
    productPosition,
    layoutType,
    travelers: order.travelers,
    shouldEnableAddToCartButton,
    isSelected
  });
  if (!product) {
    return {
      productData: null,
      rootElement,
      translation,
      snowplowTracking
    };
  }
  const {
    priceRaw,
    phonePrefixMarketTexts
  } = product;
  const {
    priceLabel,
    price,
    priceSuffix
  } = getPriceProps(t, isSelected, product);
  const {
    abstractContent,
    additionalInformation,
    readMoreContent,
    title,
    confirmText = '',
    declineText = '',
    persuasionContent = '',
    confirmationContent = '',
    upSell
  } = translation;
  const shouldShowPersuasionModal = !isOrderTypePostbooking(order) && ((_order$persuasion = order.persuasion) === null || _order$persuasion === void 0 ? void 0 : _order$persuasion.isEnabled);
  const productCardModal = getModal(shouldShowPersuasionModal, confirmationContent, persuasionContent);
  const {
    upSellObject,
    isUpsellHighlight = false,
    shouldDisplayUpsell = false
  } = getUpsellProps(upSell);
  const modalText = {
    confirmText,
    declineText
  };
  const isSmallCardLayout = layoutType === ProductCardLayoutType.Small;
  const shouldDisableValidation = getShouldDisableValidation(p, productId, layoutType);
  const isViLayout = layoutType === ProductCardLayoutType.VI;
  const isViSmallLayout = layoutType === ProductCardLayoutType.VISmall;
  const shouldUseViExtraProps = isViLayout || isViSmallLayout;
  const viExtraProps = shouldUseViExtraProps ? {
    viPromotionalPill: translation.viPromotionalPill,
    viReadMorePrefix: translation.viReadMoreText
  } : undefined;
  const selectedExtraProps = viExtraProps ? {
    ...extraProps,
    ...viExtraProps
  } : extraProps;
  const selectedIllustrationUrl = isSmallCardLayout || isViSmallLayout ? pictogramUrl : illustrationUrl;
  const productData = {
    additionalInformation,
    description: abstractContent,
    extraProps: selectedExtraProps,
    form,
    illustrationUrl: selectedIllustrationUrl,
    isHighlighted: isUpsellHighlight,
    isSelected,
    layoutType,
    modalText,
    price,
    priceLabel,
    priceRaw,
    priceSuffix,
    productCardModal,
    productCode,
    productId,
    productName,
    productPosition,
    readMoreButtonLabel,
    readMoreContent,
    sellSpecification,
    sendCartGAEvent,
    shouldDisableValidation,
    shouldDisplayUpsell,
    shouldEnableAddToCartButton,
    title,
    travelers: order.travelers,
    upSell: upSellObject
  };
  return {
    productData,
    phonePrefixMarketTexts,
    rootElement,
    translation,
    isSmallCardLayout,
    snowplowTracking
  };
};
//# sourceMappingURL=useProductData.js.map